import { currentAccessToken } from './auth';
import { config } from './config';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

// DEV MODE: mock responses when backend is unreachable
function mockResponse(method: string, path: string, body?: unknown): unknown {
  if (path === '/api/me' || path.startsWith('/api/contractors')) {
    return { contractor: { id: 'dev-001', companyName: (body as any)?.companyName ?? 'Demo Co', licenseNumber: (body as any)?.licenseNumber ?? '', phone: (body as any)?.phone ?? '', mailingAddress: (body as any)?.mailingAddress ?? '', tier: 'sub', subscriptionStatus: 'trial', freeJobsUsed: 0, permissionLevel: 'admin', mailBalanceCents: 0, mailAckAt: null, accountRole: 'contractor' } };
  }
  if (path === '/api/billing/wallet') return { balance_cents: 0, balance_dollars: 0, mail_ack_at: null, transactions: [] };
  if (path === '/api/jobs' || path.startsWith('/api/jobs')) return { jobs: [], job: null };
  if (path.startsWith('/api/lookup')) return { paVerified: false, legalOwner: null, parcelId: null, legalDescription: null, mortgageHolder: null, mortgageHolderAddress: null };
  return {};
}

async function request<T>(method: string, path: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  const token = await currentAccessToken();
  const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders };
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  try {
    const res = await fetch(`${config.apiUrl}${path}`, { method, headers, body: payload });
    const text = await res.text();
    const json = text ? JSON.parse(text) : null;
    if (!res.ok) throw new ApiError(res.status, json?.error ?? `Request failed (${res.status})`, json?.details);
    return json as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    // Backend unreachable — return mock data so UI stays navigable
    console.warn('[DEV] Backend unreachable, using mock for:', method, path);
    return mockResponse(method, path, body) as T;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),
};

// ---------------------------------------------------------------- types mirrored from the backend

/** Mirrors COUNTIES in backend/src/scrapers/types.ts — keep the two in step. */
export type County =
  | 'palm_beach'
  | 'broward'
  | 'collier'
  | 'lee'
  | 'hillsborough'
  | 'orange'
  | 'miami_dade'
  | 'duval'
  | 'polk'
  | 'pinellas'
  | 'seminole'
  | 'volusia'
  | 'brevard'
  | 'indian_river'
  | 'sarasota'
  | 'manatee'
  | 'pasco'
  | 'alachua'
  | 'baker'
  | 'bay'
  | 'bradford'
  | 'calhoun'
  | 'charlotte'
  | 'citrus'
  | 'clay'
  | 'columbia'
  | 'desoto'
  | 'dixie'
  | 'escambia'
  | 'flagler'
  | 'franklin'
  | 'gadsden'
  | 'gilchrist'
  | 'glades'
  | 'gulf'
  | 'hamilton'
  | 'hardee'
  | 'hendry'
  | 'hernando'
  | 'highlands'
  | 'holmes'
  | 'jackson'
  | 'jefferson'
  | 'lafayette'
  | 'lake'
  | 'leon'
  | 'levy'
  | 'liberty'
  | 'madison'
  | 'marion'
  | 'martin'
  | 'monroe'
  | 'nassau'
  | 'okaloosa'
  | 'okeechobee'
  | 'osceola'
  | 'putnam'
  | 'st_johns'
  | 'st_lucie'
  | 'santa_rosa'
  | 'sumter'
  | 'suwannee'
  | 'taylor'
  | 'union'
  | 'wakulla'
  | 'walton'
  | 'washington';
export const COUNTY_LABELS: Record<County, string> = {
  palm_beach: 'Palm Beach',
  broward: 'Broward',
  collier: 'Collier',
  lee: 'Lee',
  hillsborough: 'Hillsborough',
  orange: 'Orange',
  miami_dade: 'Miami-Dade',
  duval: 'Duval',
  polk: 'Polk',
  pinellas: 'Pinellas',
  seminole: 'Seminole',
  volusia: 'Volusia',
  brevard: 'Brevard',
  indian_river: 'Indian River',
  sarasota: 'Sarasota',
  manatee: 'Manatee',
  pasco: 'Pasco',
  alachua: 'Alachua',
  baker: 'Baker',
  bay: 'Bay',
  bradford: 'Bradford',
  calhoun: 'Calhoun',
  charlotte: 'Charlotte',
  citrus: 'Citrus',
  clay: 'Clay',
  columbia: 'Columbia',
  desoto: 'DeSoto',
  dixie: 'Dixie',
  escambia: 'Escambia',
  flagler: 'Flagler',
  franklin: 'Franklin',
  gadsden: 'Gadsden',
  gilchrist: 'Gilchrist',
  glades: 'Glades',
  gulf: 'Gulf',
  hamilton: 'Hamilton',
  hardee: 'Hardee',
  hendry: 'Hendry',
  hernando: 'Hernando',
  highlands: 'Highlands',
  holmes: 'Holmes',
  jackson: 'Jackson',
  jefferson: 'Jefferson',
  lafayette: 'Lafayette',
  lake: 'Lake',
  leon: 'Leon',
  levy: 'Levy',
  liberty: 'Liberty',
  madison: 'Madison',
  marion: 'Marion',
  martin: 'Martin',
  monroe: 'Monroe',
  nassau: 'Nassau',
  okaloosa: 'Okaloosa',
  okeechobee: 'Okeechobee',
  osceola: 'Osceola',
  putnam: 'Putnam',
  st_johns: 'St. Johns',
  st_lucie: 'St. Lucie',
  santa_rosa: 'Santa Rosa',
  sumter: 'Sumter',
  suwannee: 'Suwannee',
  taylor: 'Taylor',
  union: 'Union',
  wakulla: 'Wakulla',
  walton: 'Walton',
  washington: 'Washington',
};
/** Every county, alphabetical by name — for pickers. */
export const COUNTY_OPTIONS: County[] = (Object.keys(COUNTY_LABELS) as County[]).sort((x, y) =>
  COUNTY_LABELS[x].localeCompare(COUNTY_LABELS[y]),
);


export type JobStatus =
  | 'new' | 'property_verified' | 'noo_prepared' | 'noo_sent' | 'noo_delivered' | 'active'
  | 'invoiced' | 'partially_paid' | 'dispute' | 'litigation' | 'closed';

export type WorkType = 'labor_only' | 'labor_materials' | 'materials_only';
export type DeliveredTo = 'job_site' | 'contractor_yard' | 'picked_up';
/** How a Notice to Owner left the building: Lob, or the contractor's own trip to the post office. */
export type NooSendMethod = 'platform' | 'self_service';
/** How the owner's NOC signature gets notarized: Proof's remote notary, or a local one. */
export type NocMethod = 'platform_ron' | 'self_service_notary';
/** Where the Notice of Commencement stands. Same four words on both paths. */
export type NocStatus = 'not_started' | 'awaiting_notarization' | 'notarized' | 'recorded';
export type ContractorRole = 'prime' | 'gc' | 'sub';
export type PermissionLevel = 'read_only' | 'field' | 'manager' | 'admin';
/** Owner opt-in dual-control on an office seat (Run 8). Not a fifth seat. */
export type SendControl = 'off' | 'notify' | 'approve';
/** Two-brand split (Run 5): which door the account came through. */
export type AccountRole = 'contractor' | 'homeowner';
/** Run 9: a brokerage is the same office shape — the kind only swaps copy and
 * opens sale-side file oversight. Never a fifth permission level. */
export type CompanyKind = 'contractor' | 'brokerage';
export type WaiverType = 'conditional_partial' | 'unconditional_partial' | 'conditional_final' | 'unconditional_final';
export type Urgency = 'good' | 'warning' | 'urgent' | 'missed';

export interface Contractor {
  id: string;
  companyName: string;
  /** Uploaded company logo (served to its owner only), printed on their documents. */
  logoUrl?: string | null;
  /** Vestigial. Not a job role. New accounts are unspecified. */
  tier?: ContractorRole | 'unspecified';
  licenseNumber: string | null;
  licenseClaimedAt?: string | null;
  licenseVerifiedAt?: string | null;
  personVerifiedAt?: string | null;
  personVerifiedName?: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  subscriptionType: string | null;
  subscriptionStatus: string | null;
  freeJobsUsed: number | null;
  expoPushToken: string | null;
  permissionLevel: PermissionLevel;
  mailBalanceCents: number;
  mailAckAt: string | null;
  accountRole: AccountRole;
  /** Run 8: null until the office admin finishes (or skips) the first-run interview. */
  officeSetupCompletedAt?: string | null;
  /** Run 9: optional so pre-brokerage fixtures still typecheck. */
  companyKind?: CompanyKind;
  /** Run 11 early access: null until this account accepts the agreement. Every
   *  authenticated route 403s with beta_agreement_required until it is set. */
  betaAgreementAcceptedAt?: string | null;
  betaAgreementVersion?: string | null;
}

/** Addressed office invite. Login is not a claim — join is a living person. */
export interface PendingOfficeInvite {
  contractorId: string;
  companyName: string;
  permissionLevel: PermissionLevel;
  email: string;
}

/** A property a homeowner tracks (Run 5). */
export interface HomeownerProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  ownerName: string | null;
  parcelId: string | null;
  county?: string | null;
  graphParcelId?: string | null;
  legalDescription: string | null;
  mortgageHolder: string | null;
  scrapedAt: string | null;
  createdAt: string | null;
  /** Occupying-owner signal. False until they click Selling? */
  isSelling?: boolean;
  sellingSince?: string | null;
}

/** Result of POST /api/properties/scrape. */
export interface PropertyScrapeResult {
  owner: string;
  parcelId: string;
  legalDescription: string;
  mortgageHolder: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  cached: boolean;
}

export const MAIL_RATE_CENTS = 800;
export const MIN_TOPUP_CENTS = 2500;

export interface WalletTxn {
  id: string;
  amountCents: number;
  type: 'topup' | 'send_debit' | 'refund' | 'adjustment';
  description: string | null;
  balanceAfterCents: number;
  createdAt: string | null;
}

export interface Wallet {
  balance_cents: number;
  balance_dollars: number;
  mail_ack_at: string | null;
  transactions: WalletTxn[];
}

/** Response of a wallet-gated send: nothing mails until firesAt; cancel-send undoes it. */
export interface QueuedSendResult {
  docType: 'noo' | 'demand_letter';
  attemptId: string;
  firesAt: string;
  undoSeconds: number;
  costCents: number;
  balanceCents: number;
  recipientCount: number;
}

export interface Alert {
  level: 'week' | 'three_days' | 'tomorrow' | 'today' | 'passed';
  daysLeft: number;
  fireOn: string;
  title: string;
  body: string;
}

export interface Job {
  id: string;
  /** Set when archived: kept, but off the job list and the pilot's job count. */
  archivedAt?: string | null;
  status: JobStatus;
  contractorRole: ContractorRole;
  propertyAddress: string;
  county: County;
  workType: WorkType;
  /** Materials made specifically for this job (custom cabinets, countertops, trusses). */
  speciallyFabricated?: boolean;
  contractedWithName: string;
  contractedWithAddress: string;
  contractAmount: string;
  paVerified: boolean;
  legalOwner: string | null;
  legalOwnerAddress: string | null;
  parcelId: string | null;
  legalDescription: string | null;
  mortgageHolder: string | null;
  mortgageHolderAddress: string | null;
  deedReference: string | null;
  startDate: string | null;
  firstLaborDate: string | null;
  firstDeliveryDate: string | null;
  lastWorkDate: string | null;
  completionDate: string | null;
  finalComplete: boolean;
  nooDeadline: string | null;
  lienDeadline: string | null;
  lienEnforcementDeadline: string | null;
  nooSentAt: string | null;
  nooDeliveredAllAt: string | null;
  /** Null until a notice is prepared or sent; then which path it took. */
  nooSendMethod?: NooSendMethod | null;
  /** The unsigned NOO to print and mail. Mirrors nocSelfServiceUrl. */
  nooSelfServiceUrl?: string | null;
  lienFiledAt: string | null;
  lienBookPage: string | null;
  stripeChargeId: string | null;
  nocPhotoUrl: string | null;
  nocRecordedBookPage: string | null;
  nocProofTransactionId: string | null;
  nocMethod?: NocMethod | null;
  /** When the UNSIGNED NOC was produced. Never means the NOC is done. */
  nocGeneratedAt?: string | null;
  nocSelfServiceUrl?: string | null;
  nocNotarizedUrl?: string | null;
  cosEnabled: boolean;
  cosSignedAt: string | null;
  cosDocumentUrl: string | null;
  financingEligible: boolean;
  demandSentAt: string | null;
  demandResponseDue: string | null;
  createdAt: string;
  // computed
  ownerMismatch: boolean;
  nooUrgency: Urgency;
  lienUrgency: Urgency;
  currentAlert: Alert | null;
  lienFiled: boolean;
  /**
   * Mailing was handled outside LienGuardian. The job carries a standing
   * banner while this is true — we did not create the service evidence and
   * cannot guarantee service or anything downstream of it.
   */
  /**
   * The disclaimer the contractor agreed to, VERBATIM from the backend, on a
   * job that was mailed outside the platform. Null otherwise. Text rather than
   * a flag so the banner quotes the sentence the acknowledgment recorded.
   */
  selfServiceDisclaimer?: string | null;
  selfServiceAckAt?: string | null;
  /** Computed. 'awaiting_notarization' is paper only — not a notarial act. */
  nocStatus?: NocStatus;
}

export interface Recipient {
  id: string;
  recipientType: 'owner' | 'mortgage_holder' | 'contracted_with';
  name: string;
  address: string;
  lobLetterId: string | null;
  trackingNumber: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
}

export interface Payment { id: string; amount: string; receivedDate: string; cleared: boolean; isFinal: boolean }
export interface Invoice {
  id: string; invoiceNumber: number; amount: string; description: string | null; sentAt: string | null;
  dueDate: string | null; status: string; paidAmount: string | null; paidAt: string | null; payments: Payment[];
}
export interface Supplier { id: string; supplierName: string; amount: string; purchasedDate: string; paidAt: string | null }
export interface Waiver { id: string; waiverType: WaiverType; amount: string | null; signedAt: string | null; sentToEmail: string | null; documentUrl: string | null; effectiveAt: string | null }
export interface Doc {
  id: string;
  jobId: string;
  docType: string;
  pdfUrl: string | null;
  generatedAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  metaJson: string | null;
  job?: { id: string; propertyAddress: string; status: JobStatus };
  /** Printed on every page, e.g. "LG-7F3KQ-9Q2MX". Older documents have none. */
  publicId?: string | null;
  /** SHA-256 of the exact file as issued. */
  sha256?: string | null;
  verifySharedAt?: string | null;
  /** Who may verify it by ID: anyone (recorded public record / shared) or only this account. */
  verifiable?: 'public_record' | 'shared' | 'private';
}
export interface Referral { id: string; status: string; submittedAt: string; attorneyContactedAt: string | null; resolvedAt: string | null }
export interface WorkEvent { id: string; kind: string; eventDate: string; note: string | null; deliveredTo?: DeliveredTo | null }
export interface FilingChecklist { clerkName: string; recordingAddress: string; website: string; expectedFee: string; steps: string[] }

export interface JobDetail extends Job {
  /** True until anything consequential exists on the job; then archive only. */
  deletable: boolean;
  /** Why it can no longer be deleted, in words the contractor reads. */
  deleteBlockers: string[];
  recipients: Recipient[];
  /** Set when a delivery went to a yard or was picked up: keep the paper trail, ask counsel. */
  deliveryProofWarning?: string | null;
  /** Specially fabricated work with nothing delivered or installed yet: attorney review before any Claim of Lien. */
  fabricationReviewWarning?: string | null;
  invoices: Invoice[];
  suppliers: Supplier[];
  waivers: Waiver[];
  documents: Doc[];
  referrals: Referral[];
  workEvents: WorkEvent[];
  balance: { invoiced: number; paid: number; balance: number };
  unpaidSuppliers: number;
  waiverLocks: Record<WaiverType, { locked: boolean; reason: string | null }>;
  filingChecklist: FilingChecklist;
  nooAlertSchedule: Alert[];
  /** Letters an NOO will need right now (owner + mortgage? + contractor copy unless prime). */
  nooRecipientCount: number;
  /** A send waiting out its 60-second undo window, if any. */
  queuedSend: { docType: string; attemptId: string; firesAt: string; refundCents: number } | null;
  /** Self-service mailing evidence, oldest first. Empty on the platform path. */
  mailingProofs: MailingProof[];
  /** The executed NOC, once uploaded. One per job. */
  nocProof: {
    id: string;
    unsignedPdfUrl: string;
    notarizedPdfUrl: string;
    notaryName: string | null;
    notaryCommission: string | null;
    uploadedAt: string;
  } | null;
  /** Where to record the NOC, once it is notarized. Null before that. */
  erecording: ERecordingResult | null;
}

/** What the contractor brought back from the post office. */
export type MailingProofType = 'certified_mail_receipt' | 'delivery_confirmation';

export interface MailingProof {
  id: string;
  proofType: MailingProofType;
  fileUrl: string;
  uploadedAt: string;
  /** The date on the receipt or the card — what the statute measures. */
  attestedDate: string | null;
  trackingNumber: string | null;
  notes: string | null;
}

/** POST /api/jobs/:id/noo/prepare */
export interface PreparedNoo {
  job: Job;
  pdfUrl: string;
  recipients: Array<{ type: string; name: string; address: string }>;
}

/** POST /api/jobs/:id/noo/proof */
export interface MailingProofResult {
  job: Job;
  proof: Pick<MailingProof, 'id' | 'proofType' | 'fileUrl' | 'trackingNumber'>;
}

/** What ManualERecordingProvider hands back — the contractor records it themselves. */
export interface ERecordingResult {
  submissionId: string;
  status: 'submitted' | 'manual_required';
  portalUrl: string;
  instructions: string;
}

/** POST /api/jobs/:id/noc/self-service */
export interface GeneratedNoc {
  document: Doc;
  pdfUrl: string;
  job: Job;
}

/** POST /api/jobs/:id/noc/notarized */
export interface NotarizedNocResult {
  job: Job;
  proof: { id: string; unsignedPdfUrl: string; notarizedPdfUrl: string; notaryName: string | null; notaryCommission: string | null };
  document: Doc | null;
  erecording: ERecordingResult;
}

/** GET /api/jobs/:id/noc/status */
export interface NocStatusResult {
  nocStatus: NocStatus;
  nocMethod: NocMethod | null;
  unsignedPdfUrl: string | null;
  notarizedPdfUrl: string | null;
  recordedBookPage: string | null;
  notary: { name: string | null; commission: string | null; uploadedAt: string } | null;
  erecording: ERecordingResult | null;
}

export interface ParcelCandidate { parcelId: string; label: string; ownerName?: string; address?: string }
export interface PropertyRecord {
  parcelId: string; legalOwner: string; legalOwnerAddress: string | null; situsAddress: string | null;
  legalDescription: string | null; deedReference: string | null; mortgage: { holderName: string; holderAddress: string | null } | null;
}
export type ScrapeResult =
  | { status: 'found'; record: PropertyRecord }
  | { status: 'multiple'; candidates: ParcelCandidate[] }
  | { status: 'not_found'; message: string }
  | { status: 'error'; message: string };

export interface CreateJobResult { job: Job; scrape: ScrapeResult; billing: { kind: string; reason?: string } }
